import type { Master } from "@/api/masterService";
import Breadcrumb from "@/components/Breadcrumb";
import BackButton from "@/components/buttons/BackButton";
import MasterTable from "@/components/master/MasterTable";
import SaveMasterModal from "@/components/master/SaveMasterModal";
import {
  useCreateReturnScrap,
  useGetReturnScrap,
  useUpdateReturnScrap,
} from "@/hooks/masterHook";
import { usePermissions } from "@/hooks/usePermissions";
import { useQueryHandler } from "@/hooks/useQueryHandler";
import { DefaultSecondaryButton } from "@/styles/globalstyles";
import { useToast } from "@/hooks/useToast";
import { ErrorAlert } from "@/utils/alert";
import { getAxiosErrorMessage } from "@/utils/healper";
import { Add } from "@mui/icons-material";
import { Box, TextField, Button, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";

const ReturnScrapMaster = () => {
  const canEdit = usePermissions().canEdit("return.scrap");
  const { showToast } = useToast();
  const [searchFilter, setSearchFilter] = useState<string>("");
  const [openSaveModal, setOpenSaveModal] = useState<boolean>(false);
  const [selectedMaster, setSelectedMaster] = useState<Master | undefined>(
    undefined
  );

  const {
    data: returnScrapData,
    isLoading: isLoadingReturnScrap,
    isError: isErrorReturnScrap,
  } = useGetReturnScrap({}, true);

  const { mutate: createReturnScrap, isPending: isCreatingReturnScrap } =
    useCreateReturnScrap();

  const { mutate: updateReturnScrap, isPending: isUpdatingReturnScrap } =
    useUpdateReturnScrap();

  const filteredRows = useMemo(() => {
    const sortedData = [...(returnScrapData ?? [])]?.sort((a, b) => {
      if (a.is_active === b.is_active) {
        return a.name!.localeCompare(b.name!);
      }
      return a.is_active ? -1 : 1;
    });
    if (!searchFilter) {
      return sortedData || [];
    }
    return (sortedData || []).filter((row) =>
      row.name?.toLowerCase().includes(searchFilter?.toLowerCase())
    );
  }, [returnScrapData, searchFilter]);

  useQueryHandler({
    isFetching: isCreatingReturnScrap || isUpdatingReturnScrap,
    isError: isErrorReturnScrap,
    errorMessage: "เกิดข้อผิดพลาดในการโหลดข้อมูล",
  });

  useEffect(() => {
    document.title = "Return Scrap Supplier | AMPA";
  }, []);

  const handleClickAddReturnScrapSupplier = () => {
    setOpenSaveModal(true);
    setSelectedMaster(undefined);
  };

  const handleAddReturnScrapSupplier = (master: Master) => {
    createReturnScrap(master, {
      onSuccess: () => {
        showToast("เพิ่ม Return Scrap Supplier สำเร็จ", "success");
      },
      onError: (error) => {
        ErrorAlert("Error", getAxiosErrorMessage(error));
        console.log("Error creating Return Scrap Supplier:", error);
      },
    });
  };

  const handleEditReturnScrapSupplier = (master: Master) => {
    updateReturnScrap(master, {
      onSuccess: () => {
        showToast("แก้ไข Return Scrap Supplier สำเร็จ", "success");
      },
      onError: (error) => {
        ErrorAlert("Error", getAxiosErrorMessage(error));
        console.log("Error updating Return Scrap Supplier:", error);
      },
    });
  };

  return (
    <Box sx={{ px: 3, pt: 3, pb: 1 }}>
      <Breadcrumb
        current="Return Scrap Master"
        previous={[{ key: "return.scrap" }]}
      />
      <Box mt={1}>
        <Box
          display={"flex"}
          alignItems="center"
          justifyContent="flex-start"
          gap={2}
        >
          <BackButton to="/return-scrap" />
          <Typography variant="body1" fontWeight={600}>
            จัดการ Return Scrap Supplier
          </Typography>
        </Box>
        <Box sx={{ p: 1, borderRadius: 1 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box sx={{ flexGrow: 1 }}>
              <TextField
                label="ค้นหา Master"
                variant="outlined"
                size="small"
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                sx={{
                  mt: 2,
                  mb: 1,
                  width: "30%",
                  "& .MuiInputLabel-root": {
                    fontSize: "0.8rem",
                    color: "#666666",
                    "&.Mui-focused": {
                      color: "primary.main",
                    },
                  },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: "#cccccc",
                    },
                    "&:hover fieldset": {
                      borderColor: "#999999",
                    },
                  },
                }}
              />
            </Box>
            <Box gap={1} display="flex">
              <Button
                variant="outlined"
                sx={{
                  ...DefaultSecondaryButton,
                  mt: 2,
                  alignItems: "center",
                  fontSize: "0.75rem",
                }}
                onClick={() => handleClickAddReturnScrapSupplier()}
                disabled={!canEdit}
                size="small"
              >
                <Add /> เพิ่ม Return Scrap Supplier
              </Button>
            </Box>
          </Box>
          <MasterTable
            data={filteredRows}
            isLoading={isLoadingReturnScrap}
            handleEdit={(row) => () => {
              setSelectedMaster(row);
              setOpenSaveModal(true);
            }}
            canEdit={canEdit}
          />
        </Box>
      </Box>

      <SaveMasterModal
        handleAddMaster={handleAddReturnScrapSupplier}
        handleEditMaster={handleEditReturnScrapSupplier}
        handleClose={() => {
          setOpenSaveModal(false);
        }}
        open={openSaveModal}
        selectedMaster={selectedMaster}
      />
    </Box>
  );
};

export default ReturnScrapMaster;
